import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { conversationsApi } from '../api/conversations';
import type { Conversation } from '../types/api';

const KEY = ['conversations'];

export function useConversations() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => (await conversationsApi.list()).conversations,
  });
}

export function useConversation(id: string | undefined) {
  return useQuery({
    queryKey: ['conversation', id],
    queryFn: async () => {
      if (!id) throw new Error('No conversation id');
      return conversationsApi.get(id);
    },
    enabled: !!id,
  });
}

export function useCreateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (personaId: string) => conversationsApi.create(personaId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conversationsApi.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function usePatchConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fields }: { id: string; fields: { title?: string; pinned?: boolean } }) =>
      conversationsApi.patch(id, fields),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['conversation', vars.id] });
    },
  });
}

export function pinnedFirst(convs: Conversation[]): Conversation[] {
  return [...convs].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}
